import React from 'react';
import type {InputBaseComponentProps, InputLabelProps} from '@mui/material';
import type {TextValue} from '@netx/core-asset/models/attribute';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {TextField, type TextFieldProps} from '@netx/core-components/TextField';
import {usePlaceholder} from '../../../../hooks';
import type {CommonProps} from '../../types';
import Lookup from '../MetadataLookup';

export interface TextAttributeProps
	extends Omit<CommonProps<TextValue>, 'onChange' | 'value'>,
		Omit<TextFieldProps, 'name'> {
	value?: string | null;
}

export const TextAttributePrimitive = ({
	action,
	attrData,
	hideLabel,
	isMetadataLookup,
	isMetadataTarget,
	multipleValues,
	placeholder,
	readOnly,
	value,
	onLookup,
	// so it does no get passed along
	// only some of our attribute types are offer folksonomy
	disableFolksonomy,
	// only some of our attribute types are searchable
	onSearch,
	...props
}: TextAttributeProps) => {
	const cleared = action === 'clear';
	const defaultValue = value == null ? '' : value;
	const isTextArea = attrData.type === 2;
	const shrinkLabel = multipleValues || cleared;

	const textfieldPlaceholder = usePlaceholder(attrData, placeholder, {
		cleared,
		multipleValues,
		readOnly,
		value: defaultValue,
	});

	const renderInput = useConstCallback(
		(params?: {
			inputProps: InputBaseComponentProps;
			InputLabelProps: InputLabelProps;
		}) => {
			return (
				<TextField
					{...params}
					hideErrorMessage
					fullWidth
					id={attrData.attributeid.toString()}
					label={hideLabel ? undefined : attrData.name}
					name={attrData.name}
					{...props}
					multiline={isTextArea}
					placeholder={textfieldPlaceholder}
					value={defaultValue}
					InputLabelProps={{
						...params?.InputLabelProps,
						shrink: shrinkLabel ? true : undefined,
					}}
				/>
			);
		},
	);

	if (isMetadataLookup && onLookup) {
		return (
			<Lookup
				attrData={attrData}
				disabled={props.disabled}
				value={defaultValue}
				onChange={(value) =>
					props.onChange?.(
						{target: {value: value ?? ''}} as any,
						value as string,
					)
				}
				onLookup={onLookup}
				renderInput={renderInput}
			/>
		);
	}

	return renderInput();
};

export const TextAttribute = React.memo(TextAttributePrimitive);

export default TextAttribute;
